import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Report } from './Report';
import { getCurrencySymbol } from '../../../util/productUtils';

// ─────────────────────────────────────────────────────────────────────────────
// Table-based report PDFs — every section is a table (a ranked/breakdown
// table where the old version drew a hand-rolled bar/pie chart) so the
// document reads as dense, scannable data instead of decorative graphics.
// Two problems this fixes structurally rather than patching around:
//   1. Hand-drawn bar charts had no width-aware label layout, so value
//      labels routinely overlapped once there were more than a few bars
//      (see the old drawBarChart — removed).
//   2. Section headings were drawn independently of the table that followed
//      them, so a heading could land at the very bottom of a page with its
//      table starting on the next — every heading now goes through
//      sectionHeading()/twoColumnTables(), which reserve room for the
//      heading *and* the start of its table before committing to a page.
// ─────────────────────────────────────────────────────────────────────────────

export class ReportPDFGenerator {
  private static readonly COLORS = {
    // Used sparingly — the thin title accent bar and table header fills are
    // the only places color appears; everything else is text/grayscale.
    primary:   '#37474f',
    text:      '#1a1a1a',
    muted:     '#6b7280',
    lightGray: '#f4f5f6',
    border:    '#d7dbe0',
  };

  // ── formatting helpers ─────────────────────────────────────────────────────

  private static fmtAmt(n: number, sym: string): string {
    return `${sym} ${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  private static fmtNum(n: number): string {
    return n.toLocaleString('en-US');
  }

  // ── layout primitives ─────────────────────────────────────────────────────

  /** Adds a new page and resets y to the top margin if `needed` mm won't fit on the current page. */
  private static ensureSpace(doc: jsPDF, y: number, needed: number): number {
    const ph = doc.internal.pageSize.getHeight();
    if (y + needed > ph - 20) {
      doc.addPage();
      return 20;
    }
    return y;
  }

  /**
   * Draws a section heading, reserving enough room first that the heading
   * never ends up alone at the bottom of a page with its table pushed to
   * the next one. `minFollowing` is a conservative estimate of "heading +
   * table header row + a couple of body rows" — once that much is
   * guaranteed, autoTable is free to paginate the rest of a long table
   * normally.
   */
  private static sectionHeading(doc: jsPDF, y: number, title: string, minFollowing = 32): number {
    y = this.ensureSpace(doc, y, minFollowing);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(this.COLORS.text);
    doc.text(title, 15, y);
    return y + 7;
  }

  /** A plain summary/metric table — label + value rows, no ranking or share column. */
  private static metricsTable(doc: jsPDF, y: number, title: string, rows: (string | number)[][], width = 100): number {
    y = this.sectionHeading(doc, y, title, 30);
    autoTable(doc, {
      startY: y,
      head: [['Metric', 'Value']],
      body: rows,
      theme: 'plain',
      headStyles: { fillColor: this.COLORS.lightGray, textColor: this.COLORS.text, fontStyle: 'bold' },
      styles: { fontSize: 9 },
      margin: { left: 15 },
      tableWidth: width,
    });
    return (doc as any).lastAutoTable.finalY + 12;
  }

  /** A full-width detail table with a heading — the workhorse for the row-per-record tables. */
  private static detailTable(doc: jsPDF, y: number, title: string, head: string[][], body: (string | number)[][], columnStyles?: Record<number, any>): number {
    y = this.sectionHeading(doc, y, title, 34);
    autoTable(doc, {
      startY: y,
      head, body,
      theme: 'striped',
      headStyles: { fillColor: this.COLORS.primary, textColor: 255 },
      alternateRowStyles: { fillColor: this.COLORS.lightGray },
      margin: { left: 15, right: 15 },
      styles: { fontSize: 8 },
      columnStyles,
    });
    return (doc as any).lastAutoTable.finalY + 12;
  }

  /** Draws just the ranked "top N" table body (no heading) at a given startY — the piece twoColumnRow composes. */
  private static rankedTableBody(doc: jsPDF, startY: number, rows: { label: string; value: number }[], valueFormatter: (v: number) => string, valueHeader: string, x: number, width: number): number {
    const pw = doc.internal.pageSize.getWidth();
    autoTable(doc, {
      startY,
      head: [['#', 'Name', valueHeader]],
      body: rows.map((r, i) => [String(i + 1), r.label, valueFormatter(r.value)]),
      theme: 'grid',
      headStyles: { fillColor: this.COLORS.primary, textColor: 255, fontStyle: 'bold', fontSize: 8 },
      styles: { fontSize: 8 },
      margin: { left: x, right: pw - x - width },
      tableWidth: width,
      columnStyles: { 0: { cellWidth: 8 } },
    });
    return (doc as any).lastAutoTable.finalY;
  }

  /** Draws just the breakdown table body (no heading) at a given startY — the piece twoColumnRow composes. */
  private static breakdownTableBody(doc: jsPDF, startY: number, rows: { label: string; value: number }[], labelHeader: string, x: number, width: number): number {
    const pw = doc.internal.pageSize.getWidth();
    const total = rows.reduce((s, r) => s + Math.max(r.value, 0), 0) || 1;
    autoTable(doc, {
      startY,
      head: [[labelHeader, 'Count', 'Share']],
      body: rows.map(r => [r.label, this.fmtNum(r.value), `${((Math.max(r.value, 0) / total) * 100).toFixed(1)}%`]),
      theme: 'grid',
      headStyles: { fillColor: this.COLORS.primary, textColor: 255, fontStyle: 'bold', fontSize: 8 },
      styles: { fontSize: 8 },
      margin: { left: x, right: pw - x - width },
      tableWidth: width,
    });
    return (doc as any).lastAutoTable.finalY;
  }

  /** Standalone (single-column) ranked table — heading + body. Replaces the old bar chart. */
  private static rankedTable(doc: jsPDF, y: number, title: string, rows: { label: string; value: number }[], valueFormatter: (v: number) => string, valueHeader = 'Value', x = 15, width?: number): number {
    const pw = doc.internal.pageSize.getWidth();
    const tableWidth = width ?? pw - 30;
    y = this.sectionHeading(doc, y, title, 30);
    return this.rankedTableBody(doc, y, rows, valueFormatter, valueHeader, x, tableWidth);
  }

  /** Standalone (single-column) breakdown table — heading + body. Replaces the old pie chart. */
  private static breakdownTable(doc: jsPDF, y: number, title: string, rows: { label: string; value: number }[], labelHeader = 'Status', x = 15, width?: number): number {
    const pw = doc.internal.pageSize.getWidth();
    const tableWidth = width ?? pw - 30;
    y = this.sectionHeading(doc, y, title, 30);
    return this.breakdownTableBody(doc, y, rows, labelHeader, x, tableWidth);
  }

  /**
   * Places two small tables side by side (each built via a `build` callback
   * that receives the shared startY, its x, and its width, returning the
   * finalY). Both headings are drawn first so a single ensureSpace() call
   * covers the whole row and both tables share one startY — otherwise the
   * two halves could get guarded independently and land on different pages,
   * or the second could be positioned above/through the first.
   */
  private static twoColumnRow(
    doc: jsPDF, y: number,
    leftTitle: string, buildLeft: (startY: number, x: number, width: number) => number,
    rightTitle: string, buildRight: (startY: number, x: number, width: number) => number,
  ): number {
    const pw = doc.internal.pageSize.getWidth();
    const gutter = 8;
    const colWidth = (pw - 30 - gutter) / 2;
    const rightX = 15 + colWidth + gutter;

    y = this.ensureSpace(doc, y, 36);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(this.COLORS.text);
    doc.text(leftTitle, 15, y);
    doc.text(rightTitle, rightX, y);
    const tableY = y + 7;

    const leftEnd = buildLeft(tableY, 15, colWidth);
    const rightEnd = buildRight(tableY, rightX, colWidth);
    return Math.max(leftEnd, rightEnd) + 12;
  }

  // ── main entry ─────────────────────────────────────────────────────────────

  static async generatePDF(report: Report, currency = 'LKR'): Promise<void> {
    const sym  = getCurrencySymbol(currency);
    const doc  = new jsPDF();
    const pw   = doc.internal.pageSize.getWidth();
    const ph   = doc.internal.pageSize.getHeight();
    let y      = 20;

    // Slim title block — a thin accent rule instead of a full-width solid
    // color band, so color is used as an accent rather than the dominant
    // visual element, and the extra ~30mm goes to data instead.
    doc.setTextColor(this.COLORS.text);
    doc.setFontSize(18); doc.setFont('helvetica', 'bold');
    doc.text(report.reportName, 15, 18);
    doc.setDrawColor(this.COLORS.primary);
    doc.setLineWidth(1);
    doc.line(15, 22, pw - 15, 22);
    doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(this.COLORS.muted);
    doc.text(`Generated ${report.generatedAt?.toLocaleString()} by ${report.generatedByName}`, 15, 28);
    y = 38;

    doc.setTextColor(this.COLORS.text);
    doc.setFontSize(10);

    if (report.description) {
      doc.setFont('helvetica', 'bold'); doc.text('Description:', 15, y);
      doc.setFont('helvetica', 'normal');
      const lines = doc.splitTextToSize(report.description, pw - 30);
      doc.text(lines, 15, y + 5);
      y += lines.length * 5 + 8;
    }

    if (report.filters && Object.keys(report.filters).length > 0) {
      doc.setFont('helvetica', 'bold'); doc.text('Filters Applied:', 15, y); y += 6;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
      if (report.filters.dateFrom) { doc.text(`Date From: ${report.filters.dateFrom.toLocaleDateString()}`, 20, y); y += 5; }
      if (report.filters.dateTo)   { doc.text(`Date To: ${report.filters.dateTo.toLocaleDateString()}`,     20, y); y += 5; }
      y += 4; doc.setFontSize(10);
    }

    switch (report.reportType) {
      case 'sales':                  this.generateSalesReport(doc, report, y, sym);                break;
      case 'inventory':              this.generateInventoryReport(doc, report, y, sym);            break;
      case 'purchase_orders':        this.generateImportOrderReport(doc, report, y, sym);        break;
      case 'low_stock':              this.generateLowStockReport(doc, report, y, sym);             break;
      case 'warehouse_performance':  this.generateWarehousePerformanceReport(doc, report, y, sym); break;
      case 'product_performance':    this.generateProductPerformanceReport(doc, report, y, sym);   break;
      case 'revenue':                this.generateRevenueReport(doc, report, y, sym);              break;
      case 'returns_cancellations':  this.generateReturnsCancellationsReport(doc, report, y, sym); break;
      case 'profit_loss':            this.generateProfitLossReport(doc, report, y, sym);           break;
    }

    // Page footers
    const total = doc.internal.pages.length - 1;
    for (let i = 1; i <= total; i++) {
      doc.setPage(i);
      doc.setFontSize(8); doc.setTextColor(128, 128, 128);
      doc.text(`Page ${i} of ${total}`, pw / 2, ph - 10, { align: 'center' });
    }

    doc.save(`${report.reportName.replace(/\s+/g, '_')}_${Date.now()}.pdf`);
  }

  // ── Sales ──────────────────────────────────────────────────────────────────

  private static generateSalesReport(doc: jsPDF, report: Report, y: number, sym: string): void {
    const data = report.data;
    y = this.metricsTable(doc, y, 'Summary', [
      ['Total Sales',         this.fmtNum(data.summary.totalOrders)],
      ['Total Revenue',       this.fmtAmt(data.summary.totalRevenue, sym)],
      ['Average Sale Price',  this.fmtAmt(data.summary.averageOrderValue, sym)],
      ['Trade-In Credit',     this.fmtAmt(data.summary.totalDiscount, sym)],
    ], 90);

    const hasTop = data.summary.topProducts?.length > 0;
    const hasStatus = data.summary.statusBreakdown && Object.keys(data.summary.statusBreakdown).length > 0;
    if (hasTop && hasStatus) {
      y = this.twoColumnRow(doc, y,
        'Best-Selling Vehicles (Top 5)', (sy, x, w) => this.rankedTableBody(doc, sy, data.summary.topProducts.slice(0, 5).map((p: any) => ({ label: p.productName, value: p.revenue })), (v) => this.fmtAmt(v, sym), 'Revenue', x, w),
        'Status Breakdown', (sy, x, w) => this.breakdownTableBody(doc, sy, Object.entries(data.summary.statusBreakdown as Record<string, number>).map(([label, value]) => ({ label, value: value as number })), 'Status', x, w),
      );
    } else if (hasTop) {
      y = this.rankedTable(doc, y, 'Best-Selling Vehicles (Top 5)', data.summary.topProducts.slice(0, 5).map((p: any) => ({ label: p.productName, value: p.revenue })), (v) => this.fmtAmt(v, sym), 'Revenue') + 12;
    } else if (hasStatus) {
      y = this.breakdownTable(doc, y, 'Status Breakdown', Object.entries(data.summary.statusBreakdown as Record<string, number>).map(([label, value]) => ({ label, value: value as number }))) + 12;
    }

    this.detailTable(doc, y, 'Sold Vehicles',
      [['VIN', 'Buyer', 'Sale Date', 'Price', 'Payment', 'Status']],
      data.orders.map((o: any) => [
        o.orderNumber, o.customerName,
        o.orderDate instanceof Date ? o.orderDate.toLocaleDateString() : 'N/A',
        this.fmtAmt(o.totalAmount, sym), o.paymentMethod, o.status,
      ]),
    );
  }

  // ── Inventory ──────────────────────────────────────────────────────────────

  private static generateInventoryReport(doc: jsPDF, report: Report, y: number, sym: string): void {
    const data = report.data;
    y = this.metricsTable(doc, y, 'Summary', [
      ['Total Products',      this.fmtNum(data.summary.totalProducts)],
      ['Total Stock',         this.fmtNum(data.summary.totalStock)],
      ['Stock Value',         this.fmtAmt(data.summary.totalValue, sym)],
      ['In Stock Items',      this.fmtNum(data.summary.inStockItems)],
      ['Out of Stock Items',  this.fmtNum(data.summary.outOfStockItems)],
    ], 90);

    if (data.products.length > 0) {
      const statusCounts: Record<string, number> = {};
      const categoryValue: Record<string, number> = {};
      data.products.forEach((p: any) => {
        statusCounts[p.status] = (statusCounts[p.status] || 0) + 1;
        categoryValue[p.category] = (categoryValue[p.category] || 0) + (p.stockValue || 0);
      });
      const categoryData = Object.entries(categoryValue).sort((a, b) => (b[1] as number) - (a[1] as number)).slice(0, 6);

      y = this.twoColumnRow(doc, y,
        'Stock Status', (sy, x, w) => this.breakdownTableBody(doc, sy, Object.entries(statusCounts).map(([label, value]) => ({ label: label.replace('_', ' '), value })), 'Status', x, w),
        'Stock Value by Category (Top 6)', (sy, x, w) => this.rankedTableBody(doc, sy, categoryData.map(([label, value]) => ({ label, value: value as number })), (v) => this.fmtAmt(v, sym), 'Value', x, w),
      );
    }

    this.detailTable(doc, y, 'Products',
      [['Product', 'SKU', 'Category', 'Stock', 'Value', 'Status']],
      data.products.map((p: any) => [
        p.productName + (p.hasVariants ? ` (${p.variants?.length || 0} variants)` : ''),
        p.sku, p.category, this.fmtNum(p.totalStock), this.fmtAmt(p.stockValue, sym), p.status.replace('_', ' '),
      ]),
    );
  }

  // ── Purchase Orders ────────────────────────────────────────────────────────

  private static generateImportOrderReport(doc: jsPDF, report: Report, y: number, sym: string): void {
    const data = report.data;
    if (!data.purchaseOrders || data.purchaseOrders.length === 0) {
      doc.setFont('helvetica', 'normal'); doc.setFontSize(12);
      doc.text('No purchase orders found for the selected criteria.', 15, y);
      return;
    }

    y = this.metricsTable(doc, y, 'Summary', [
      ['Total Purchase Orders', this.fmtNum(data.summary.totalPOs)],
      ['Total Amount',          this.fmtAmt(data.summary.totalAmount || 0, sym)],
      ['Total Paid',            this.fmtAmt(data.summary.totalPaid   || 0, sym)],
      ['Total Pending',         this.fmtAmt((data.summary.totalAmount || 0) - (data.summary.totalPaid || 0), sym)],
    ], 90);

    if (data.summary.statusBreakdown && Object.keys(data.summary.statusBreakdown).length > 0) {
      y = this.breakdownTable(doc, y, 'Status Breakdown',
        Object.entries(data.summary.statusBreakdown as Record<string, number>).map(([label, value]) => ({ label, value: value as number })),
        'Status', 15, 90) + 12;
    }

    if (data.summary.topSuppliers?.length > 0) {
      y = this.rankedTable(doc, y, 'Top Suppliers by Amount (Top 5)',
        data.summary.topSuppliers.slice(0, 5).map((s: any) => ({ label: s.supplierName, value: s.totalAmount })),
        (v) => this.fmtAmt(v, sym), 'Amount') + 12;
    }

    this.detailTable(doc, y, 'Purchase Orders',
      [['PO #', 'Supplier', 'Order Date', 'Exp. Delivery', 'Amount', 'Status', 'Payment']],
      data.purchaseOrders.map((po: any) => [
        po.poNumber || 'N/A', po.supplierName || 'Unknown',
        po.orderDate instanceof Date ? po.orderDate.toLocaleDateString() : 'N/A',
        po.expectedDelivery instanceof Date ? po.expectedDelivery.toLocaleDateString() : 'N/A',
        this.fmtAmt(po.totalAmount || 0, sym), po.status || 'draft', po.paymentStatus || 'pending',
      ]),
      { 0: { cellWidth: 28 }, 1: { cellWidth: 38 }, 2: { cellWidth: 24 }, 3: { cellWidth: 24 }, 4: { cellWidth: 26 }, 5: { cellWidth: 22 }, 6: { cellWidth: 22 } },
    );
  }

  // ── Low Stock ──────────────────────────────────────────────────────────────

  private static generateLowStockReport(doc: jsPDF, report: Report, y: number, sym: string): void {
    const data = report.data;
    y = this.metricsTable(doc, y, 'Summary', [
      ['Low Stock Items',      this.fmtNum(data.summary.totalLowStockItems)],
      ['Out of Stock Items',   this.fmtNum(data.summary.totalOutOfStockItems)],
      ['Affected Products',    this.fmtNum(data.summary.affectedProducts)],
      ['Affected Show Rooms',  this.fmtNum(data.summary.affectedWarehouses)],
    ], 90);

    if (data.items.length > 0) {
      const shortfalls = [...data.items].sort((a: any, b: any) => b.deficit - a.deficit).slice(0, 8)
        .map((it: any) => ({ label: it.productName, value: it.deficit }));
      y = this.twoColumnRow(doc, y,
        'Low Stock vs Out of Stock', (sy, x, w) => this.breakdownTableBody(doc, sy, [
          { label: 'Low Stock', value: data.summary.totalLowStockItems },
          { label: 'Out of Stock', value: data.summary.totalOutOfStockItems },
        ], 'Status', x, w),
        'Biggest Shortfalls (Top 8)', (sy, x, w) => this.rankedTableBody(doc, sy, shortfalls, (v) => this.fmtNum(v), 'Deficit', x, w),
      );
    }

    this.detailTable(doc, y, 'Low Stock Items',
      [['Product', 'Show Room', 'Current', 'Min', 'Deficit', 'Status']],
      data.items.map((item: any) => [
        item.productName + (item.variantName ? ` (${item.variantName})` : ''),
        item.warehouseName, this.fmtNum(item.currentStock), this.fmtNum(item.minStock), this.fmtNum(item.deficit),
        item.status === 'out_of_stock' ? 'Out of Stock' : 'Low Stock',
      ]),
    );
  }

  // ── Warehouse Performance ──────────────────────────────────────────────────

  private static generateWarehousePerformanceReport(doc: jsPDF, report: Report, y: number, sym: string): void {
    const data = report.data;
    y = this.metricsTable(doc, y, 'Summary', [
      ['Total Show Rooms',   this.fmtNum(data.summary.totalWarehouses)],
      ['Total Capacity',     this.fmtNum(data.summary.totalCapacity)],
      ['Total Used',         this.fmtNum(data.summary.totalUsed)],
      ['Total Available',    this.fmtNum(data.summary.totalAvailable)],
      ['Avg Utilization',    `${data.summary.averageUtilization}%`],
      ['Most Utilized',      data.summary.mostUtilizedWarehouse],
      ['Least Utilized',     data.summary.leastUtilizedWarehouse],
    ], 100);

    // No separate capacity-utilization summary here — the Show Rooms table
    // below already lists utilization % per show room, so a chart/ranking
    // duplicating it would just be the same numbers rendered twice.
    this.detailTable(doc, y, 'Show Rooms',
      [['Show Room', 'Code', 'Products', 'Stock', 'Capacity', 'Utilization']],
      data.warehouses.map((w: any) => [
        w.warehouseName, w.code, this.fmtNum(w.totalProducts), this.fmtNum(w.totalStock),
        `${this.fmtNum(w.capacityUsed)}/${this.fmtNum(w.capacityTotal)}`, `${w.utilizationRate}%`,
      ]),
    );
  }

  // ── Product Performance ────────────────────────────────────────────────────

  private static generateProductPerformanceReport(doc: jsPDF, report: Report, y: number, sym: string): void {
    const data = report.data;
    y = this.metricsTable(doc, y, 'Summary', [
      ['Total Products',      this.fmtNum(data.summary.totalProducts)],
      ['Total Revenue',       this.fmtAmt(data.summary.totalRevenue, sym)],
      ['Total Units Sold',    this.fmtNum(data.summary.totalUnitsSold)],
      ['Best Selling Product',data.summary.bestSellingProduct],
      ['Avg Stock Turnover',  data.summary.averageStockTurnover.toString()],
      ['Avg Discount',        `${data.summary.averageDiscount.toFixed(2)}%`],
    ], 100);

    if (data.products.length > 0) {
      y = this.rankedTable(doc, y, 'Top Models by Revenue (Top 8)',
        [...data.products].sort((a: any, b: any) => b.revenue - a.revenue).slice(0, 8).map((p: any) => ({ label: p.productName, value: p.revenue })),
        (v) => this.fmtAmt(v, sym), 'Revenue') + 12;
    }

    this.detailTable(doc, y, 'Products',
      [['Product', 'Category', 'Sold', 'Revenue', 'Avg Price', 'Turnover']],
      data.products.map((p: any) => [
        p.productName, p.category, this.fmtNum(p.totalSold),
        this.fmtAmt(p.revenue, sym), this.fmtAmt(p.averagePrice, sym), p.stockTurnover.toString(),
      ]),
    );
  }

  // ── Revenue ────────────────────────────────────────────────────────────────

  private static generateRevenueReport(doc: jsPDF, report: Report, y: number, sym: string): void {
    const data = report.data;
    y = this.metricsTable(doc, y, 'Summary', [
      ['Total Revenue',       this.fmtAmt(data.summary.totalRevenue, sym)],
      ['Net Revenue',         this.fmtAmt(data.summary.totalNetRevenue, sym)],
      ['Total Orders',        this.fmtNum(data.summary.totalOrders)],
      ['Avg Order Value',     this.fmtAmt(data.summary.averageOrderValue, sym)],
      ['Avg Daily Revenue',   this.fmtAmt(data.summary.averageDailyRevenue, sym)],
      ['Total Tax',           this.fmtAmt(data.summary.totalTax, sym)],
      ['Total Shipping',      this.fmtAmt(data.summary.totalShipping, sym)],
      ['Total Discount',      this.fmtAmt(data.summary.totalDiscount, sym)],
      ['Growth Rate',         `${data.summary.growthRate.toFixed(2)}%`],
      ['Highest Revenue Day', data.summary.highestRevenueDay],
      ['Lowest Revenue Day',  data.summary.lowestRevenueDay],
    ], 100);

    // No separate revenue-trend chart or category-share pie — Daily Revenue
    // below lists every day exhaustively, and Top Categories already shows
    // revenue + % of total per category, so a chart here would just repeat
    // numbers already on the page.
    if (data.topProducts?.length > 0) {
      y = this.detailTable(doc, y, 'Top Products by Revenue',
        [['Product', 'Units Sold', 'Revenue', '% of Total']],
        data.topProducts.slice(0, 10).map((p: any) => [
          p.productName, this.fmtNum(p.totalSold), this.fmtAmt(p.revenue, sym), `${p.percentageOfTotal.toFixed(2)}%`,
        ]),
      );
    }

    if (data.topCategories?.length > 0) {
      y = this.detailTable(doc, y, 'Top Categories by Revenue',
        [['Category', 'Revenue', '% of Total']],
        data.topCategories.slice(0, 10).map((c: any) => [
          c.categoryName, this.fmtAmt(c.revenue, sym), `${c.percentageOfTotal.toFixed(2)}%`,
        ]),
      );
    }

    this.detailTable(doc, y, 'Daily Revenue',
      [['Date', 'Orders', 'Revenue', 'Net Revenue', 'Avg Order', 'Tax', 'Shipping']],
      data.dailyRevenue.map((d: any) => [
        d.date instanceof Date ? d.date.toLocaleDateString() : 'N/A',
        this.fmtNum(d.totalOrders), this.fmtAmt(d.totalRevenue, sym), this.fmtAmt(d.netRevenue, sym),
        this.fmtAmt(d.averageOrderValue, sym), this.fmtAmt(d.totalTax, sym), this.fmtAmt(d.totalShipping, sym),
      ]),
      { 0: { cellWidth: 24 }, 1: { cellWidth: 18 }, 2: { cellWidth: 28 }, 3: { cellWidth: 28 }, 4: { cellWidth: 24 }, 5: { cellWidth: 20 }, 6: { cellWidth: 22 } },
    );
  }

  // ── Returns & Cancellations ────────────────────────────────────────────────

  private static generateReturnsCancellationsReport(doc: jsPDF, report: Report, y: number, sym: string): void {
    const data = report.data;
    y = this.metricsTable(doc, y, 'Summary', [
      ['Cancelled',            this.fmtNum(data.summary.totalCancelled)],
      ['Gone Cold (Lost)',     this.fmtNum(data.summary.totalRefunded)],
      ['Reservations Reviewed', this.fmtNum(data.summary.totalOrders)],
      ['Lost Revenue',         this.fmtAmt(data.summary.totalLostRevenue, sym)],
      ['Cancelled/Lost Rate',  `${data.summary.cancellationRate}%`],
    ], 90);

    if (data.orders?.length > 0) {
      // No separate "deals lost by model" ranking here — the Most
      // Cancelled/Lost Models table below already ranks the same data with
      // revenue attached.
      y = this.breakdownTable(doc, y, 'Reservation Outcome', [
        { label: 'Returned', value: data.summary.totalReturned || 0 },
        { label: 'Cancelled', value: data.summary.totalCancelled || 0 },
        { label: 'Gone Cold', value: data.summary.totalRefunded || 0 },
      ], 'Outcome', 15, 90) + 12;
    }

    if (data.summary.topReturnedProducts?.length > 0) {
      y = this.detailTable(doc, y, 'Most Cancelled / Lost Models',
        [['Model', 'Deals Lost', 'Lost Revenue']],
        data.summary.topReturnedProducts.slice(0, 10).map((p: any) => [
          p.productName, this.fmtNum(p.quantity), this.fmtAmt(p.revenue, sym),
        ]),
      );
    }

    this.detailTable(doc, y, 'Cancelled & Lost Reservations',
      [['Vehicle', 'Customer', 'Date', 'Amount', 'Status', 'Payment', 'Reason']],
      data.orders.map((o: any) => [
        o.orderNumber, o.customerName,
        o.orderDate instanceof Date ? o.orderDate.toLocaleDateString() : 'N/A',
        this.fmtAmt(o.totalAmount, sym), o.status, o.paymentMethod, o.reason || '—',
      ]),
      { 0: { cellWidth: 26 }, 1: { cellWidth: 32 }, 2: { cellWidth: 22 }, 3: { cellWidth: 28 }, 4: { cellWidth: 22 }, 5: { cellWidth: 24 }, 6: { cellWidth: 28 } },
    );
  }

  // ── Profit & Loss ──────────────────────────────────────────────────────────

  private static generateProfitLossReport(doc: jsPDF, report: Report, y: number, sym: string): void {
    const data = report.data;
    const s = data.summary;

    // No separate Revenue/COGS/Profit chart — every one of those figures is
    // already a row in this Summary table, so a chart repeating them would
    // add color without adding information.
    y = this.metricsTable(doc, y, 'Summary', [
      ['Period',                     `${s.periodDays} day(s)`],
      ['Delivered Orders',           this.fmtNum(s.totalOrders)],
      ['Revenue',                    this.fmtAmt(s.revenue, sym)],
      ['Cost of Goods Sold (COGS)',  this.fmtAmt(s.cogs, sym)],
      ['Gross Profit',               this.fmtAmt(s.grossProfit, sym)],
      ['Gross Margin',               `${s.grossMarginPercent.toFixed(2)}%`],
      ['Operating Costs',            this.fmtAmt(s.operatingCosts, sym)],
      ['  — One-Time',               this.fmtAmt(s.operatingCostsOneTime, sym)],
      ['  — Recurring (prorated)',   this.fmtAmt(s.operatingCostsRecurring, sym)],
      ['Net Profit',                 this.fmtAmt(s.netProfit, sym)],
      ['Net Margin',                 `${s.netMarginPercent.toFixed(2)}%`],
    ], 100);

    if (data.costBreakdown?.length > 0) {
      y = this.detailTable(doc, y, 'Operating Costs by Category',
        [['Category', 'One-Time', 'Recurring', 'Total', 'Entries']],
        data.costBreakdown.map((c: any) => [
          c.category, this.fmtAmt(c.oneTimeAmount, sym), this.fmtAmt(c.recurringAmount, sym),
          this.fmtAmt(c.amount, sym), this.fmtNum(c.count),
        ]),
      );
    }

    if (data.revenueByProduct?.length > 0) {
      this.detailTable(doc, y, 'Revenue & Profit by Product',
        [['Product', 'Qty Sold', 'Revenue', 'COGS', 'Gross Profit']],
        data.revenueByProduct.map((p: any) => [
          p.productName, this.fmtNum(p.quantitySold), this.fmtAmt(p.revenue, sym),
          this.fmtAmt(p.cogs, sym), this.fmtAmt(p.grossProfit, sym),
        ]),
      );
    } else {
      y = this.sectionHeading(doc, y, 'Revenue & Profit by Product', 20);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
      doc.text('No delivered orders in this date range.', 15, y);
    }
  }
}
